import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { get_all_recordings } from "../../api";
import type { RecordingSyncStatus } from "../../api/types";
import { usePageAsk } from "../../chat/AskContext";
import { pageDateFilters } from "../../chat/askScope";
import TeamActivity from "./TeamActivity";
import DashboardDateRange from "./DashboardDateRange";
import RecordingSyncRow from "./RecordingSyncRow";
import { useActivityDates } from "./useActivityDates";
import { localDayBounds } from "./dateSelection";
import { activityCoveragePeriod } from "./activityModel";
import { useRecordingList } from "./useRecordingList";

export default function RecentRecordingsSection({
  currentUserId,
  activeUserIds,
  userOptions,
  peopleControl,
  autoAnalyze,
  recordingSyncStatus,
  recordingSyncCompletionId,
  onRetryRecordingSync,
  usersLoading = false,
}: {
  currentUserId: string;
  activeUserIds: string[];
  userOptions: Array<{ id: string; label: string }>;
  /** Shared people picker and activity scope switch. */
  peopleControl?: React.ReactNode;
  autoAnalyze: boolean;
  recordingSyncStatus: RecordingSyncStatus;
  recordingSyncCompletionId: number;
  onRetryRecordingSync: () => Promise<void>;
  usersLoading?: boolean;
}) {
  const { t } = useTranslation();
  const dates = useActivityDates();
  const key = JSON.stringify([dates.from, dates.to]);
  const fetchRange = useCallback(
    async () => (await get_all_recordings(localDayBounds(dates.from, dates.to))).recordings,
    [dates.from, dates.to],
  );
  const { recordings, loadedKey, loading, error, updateError, revision, refresh } =
    useRecordingList({
      fetch: fetchRange,
      key,
      ownerId: currentUserId,
      autoAnalyze,
      recordingSyncCompletionId,
    });
  const chatDates = pageDateFilters(dates.from, dates.to);
  usePageAsk({
    sessionKey: "recordings:list",
    datePreset: dates.preset,
    page: { kind: "team" },
    label: `${userOptions
      .filter(person => activeUserIds.includes(person.id))
      .map(person => person.label)
      .join(
        ", ",
      )} · ${dates.from || dates.to ? `${dates.from} – ${dates.to}` : t("dashboard.all_time")}`,
    filters: { userIds: activeUserIds, ...chatDates },
    empty: usersLoading || !chatDates || !activeUserIds.length,
    suggestions: [t("watchlist.suggestion_apps"), t("watchlist.suggestion_rework")],
  });
  // TeamActivity memoises its calendar on these; fresh arrays every render would defeat it.
  const teamRecordings = useMemo(() => {
    const active = new Set(activeUserIds);
    const mine = recordings.filter(item => active.has(item.user_id));
    return {
      included: mine.filter(item => item.include_in_processes),
      archived: mine.filter(item => !item.include_in_processes),
    };
  }, [recordings, activeUserIds]);
  const teamPeople = useMemo(
    () => userOptions.filter(person => activeUserIds.includes(person.id)),
    [userOptions, activeUserIds],
  );

  return (
    <section className="space-y-6">
      <div className="dashboard-page-header">
        {/* The description wraps before the filters do, so they stay beside the title. */}
        <div className="min-w-0 flex-[1_1_20rem]">
          <h1 className="dashboard-page-title">{t("activity.team_title")}</h1>
          <p className="dashboard-page-description">{t("activity.team_subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {peopleControl}
          <DashboardDateRange {...dates} activityUserIds={activeUserIds} />
        </div>
      </div>
      <RecordingSyncRow status={recordingSyncStatus} onRetry={onRetryRecordingSync} />
      {updateError && (
        <p role="alert" className="text-sm text-warning">
          {t("recordings_update_failed")}{" "}
          <button onClick={refresh} className="underline">
            {t("retry")}
          </button>
        </p>
      )}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {t("unable_to_load_recordings")}{" "}
          <button onClick={refresh} className="underline">
            {t("retry")}
          </button>
        </p>
      ) : loading || usersLoading || loadedKey !== key ? (
        <p role="status" className="text-sm text-text-dim">
          {t("loading_recordings")}
        </p>
      ) : (
        <TeamActivity
          recordings={teamRecordings.included}
          archivedRecordings={teamRecordings.archived}
          people={teamPeople}
          from={dates.from}
          to={dates.to}
          coveragePeriod={activityCoveragePeriod(recordings, dates.from, dates.to)}
          revision={revision}
        />
      )}
    </section>
  );
}
